// Core types
export type {
  CheckoutOptions,
  PaymentCheckout,
  ResumeCheckoutOptions,
} from "./checkout/types";

// Paystack normalizers and transformers
export {
  convertSubunitsToMajor,
  derivePaystackEventId,
  getCustomerDisplayName,
  normalizePaystackStatus,
  transformPaystackAuthorization,
  transformPaystackCustomer,
  transformPaystackSubscription,
  transformPaystackTransaction,
} from "./processor/paystack/normalize";

// Paystack API types (for portal UI)
export type {
  CustomerData,
  InvoiceData,
  PaymentMethodData,
  PaystackApiResponse,
  PaystackAuthorization,
  PaystackCustomer,
  PaystackPlan,
  PaystackSubscription,
  PaystackTransaction,
  PlanData,
  SubscriptionData,
} from "./processor/paystack/types";
export { PortalApiError } from "./processor/paystack/types";

// Paystack webhook types
export type {
  AnyPaystackEvent,
  ChargeSuccessEvent,
  InvoiceCreateEvent,
  InvoicePaymentFailedEvent,
  InvoiceUpdateEvent,
  PaystackEnvelope,
  PaystackEventType,
  RefundFailedEvent,
  RefundPendingEvent,
  RefundProcessedEvent,
  RefundProcessingEvent,
  SubscriptionCreateEvent,
  SubscriptionDisableEvent,
  SubscriptionExpiringCardsEvent,
  SubscriptionNotRenewEvent,
  SubscriptionTransferEvent,
} from "./processor/paystack/webhook-types";
// Paystack webhook schemas — the source of truth the types above are derived from
export {
  isKnownPaystackEventType,
  PAYSTACK_EVENT_DATA_SCHEMAS,
  paystackEnvelopeSchema,
} from "./processor/paystack/webhook-types";
export type {
  BillingApp,
  BillingPlan,
  BillingProcessor,
  BillingSubscription,
  BridgeLimits,
  InvoicesResponse,
  PaymentEvent,
  PlanInterval,
  PlanType,
  SubscriptionStatus,
} from "./types";

// NOTE: verifyPaystackSignature is NOT exported from root — import directly:
// import { verifyPaystackSignature } from '@pex/billing/processor/paystack/verify'

// NOTE: Paystack API functions (chargePaystackAuthorization, createPaystackPlan, etc.)
// are server-only — import from '@pex/billing/server' instead.
export {
  derivePolarEventId,
  normalizePolarStatus,
} from "./processor/polar/normalize";
export type {
  PolarCustomer,
  PolarPrice,
  PolarProduct,
  PolarSubscription,
} from "./processor/polar/types";
// Polar types (stubs)
export type {
  AnyPolarEvent,
  PolarSubscriptionActiveEvent,
  PolarSubscriptionCanceledEvent,
  PolarSubscriptionCreatedEvent,
  PolarSubscriptionRevokedEvent,
  PolarSubscriptionUncanceledEvent,
  PolarSubscriptionUpdatedEvent,
} from "./processor/polar/webhook-types";
export type { BillingAccount } from "./queries/accounts";
export {
  getAccountByEmail,
  getAccountById,
  setPaystackCustomerCode,
} from "./queries/accounts";
// DB query helpers
export type {
  PaystackAuthorizationRow,
  UpsertAuthorizationInput,
} from "./queries/authorizations";
export {
  getValidAuthorizations,
  upsertPaystackAuthorization,
} from "./queries/authorizations";
export type {
  GetPaymentHistoryOptions,
  InsertPaymentEventInput,
  InsertPaymentEventResult,
} from "./queries/payment-events";
export {
  getMemberCharges,
  getPaymentHistory,
  hasPriorEventOfType,
  insertPaymentEvent,
  updatePaymentEventSubscriptionId,
} from "./queries/payment-events";
// NOTE: plans query helpers (getActivePlans, getPlanByProcessorId, etc.)
// are server-only — import from '@pex/billing/server' instead.
export {
  createRetryEntry,
  resolveRetry,
  retryExistsForInvoice,
} from "./queries/retries";
export type {
  CreatePendingSubscriptionInput,
  InsertSubscriptionChangeInput,
} from "./queries/subscriptions";
export {
  activatePendingSubscription,
  claimPlanChange,
  createOrUpdatePendingSubscription,
  getActiveSubscriptionByAccount,
  getOrgSubscription,
  getOrgSubscriptions,
  getPendingSubscription,
  getSubscriptionByProcessorId,
  incrementRetryCount,
  insertSubscriptionChange,
  releasePlanChange,
  resetRetryCount,
  updateSubscriptionById,
  updateSubscriptionFromWebhook,
} from "./queries/subscriptions";
export type { DonationFrequency } from "./utils/donations";
export {
  DONATION_FREQUENCIES,
  DONATION_MAX_RANDS,
  DONATION_MIN_RANDS,
  isValidDonationAmount,
} from "./utils/donations";
// Utility functions
export {
  getRemainingCapacity,
  withinLimit,
} from "./utils/limits";
export { effectiveMemberCost } from "./utils/member-pricing";
export type { PlanTrack } from "./utils/plan-mapping";
export {
  getPlanLimits,
  getPlanTierLabel,
  getPlanTrack,
  getSemanticPlanKey,
  PLAN_TIER_ORDER,
} from "./utils/plan-mapping";
export {
  BRIDGE_EXTRA_FACILITATOR_RATE_ZAR,
  CYCLE_DAYS,
  calculateCustomPlanAmount,
  calculateProrationCharge,
  formatCustomPlanName,
  formatPlanNameForLimits,
} from "./utils/pricing";
export { deriveProcessorMode } from "./utils/processor-mode";
export {
  generateSponsorshipToken,
  verifySponsorshipToken,
} from "./utils/sponsorship-token";
export { isSubscriptionAccessible } from "./utils/status";
